package com.anjuros.website.templates.template10.sections

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.templates.template10.components.Template10Reveal
import com.anjuros.website.templates.template10.components.Template10Seal
import com.anjuros.website.templates.template10.components.Template10Title
import com.anjuros.website.templates.template10.components.template10BodyFont
import com.anjuros.website.templates.template10.components.template10HeadingFont
import com.anjuros.website.templates.template10.modules.Template10Benefit
import com.anjuros.website.templates.template10.modules.Template10Section
import com.anjuros.website.templates.template10.modules.Template10Theme
import com.anjuros.website.templates.template10.modules.getColor
import com.anjuros.website.templates.template10.styles.Template10CardHoverStyle
import com.anjuros.website.templates.template10.styles.Template10ContainerStyle
import com.anjuros.website.templates.template10.styles.Template10GlyphStyle
import com.anjuros.website.templates.template10.styles.Template10SectionPaddingStyle
import com.anjuros.website.templates.template10.util.ResKey
import com.anjuros.website.templates.template10.util.Template10Glyphs
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun Template10BenefitsSection() {
    Column(
        modifier = Template10SectionPaddingStyle.toModifier()
            .id(Template10Section.Benefits.id)
            .fillMaxWidth()
            .backgroundColor(Template10Theme.Paper.rgb),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Template10Reveal(contentAlignment = Alignment.TopCenter) {
            Column(
                modifier = Modifier.gap(16.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Template10Seal(glyph = Template10Glyphs.BENEFITS)
                Template10Title(
                    text = ResKey.TEMPLATE10_BENEFITS_TITLE,
                    textAlign = TextAlign.Center
                )
            }
        }

        SimpleGrid(
            numColumns = numColumns(base = 1, md = 3),
            modifier = Template10ContainerStyle.toModifier()
                .margin(top = 64.px)
                .gap(40.px)
        ) {
            Template10Benefit.entries.forEach { benefit ->
                Template10Reveal(
                    modifier = Modifier.fillMaxWidth().fillMaxHeight(),
                    delayMs = benefit.ordinal * 150
                ) {
                    Template10Scroll(benefit)
                }
            }
        }
    }
}

/** A hanging scroll: dark rods top and bottom, paper in between. */
@Composable
private fun Template10Scroll(benefit: Template10Benefit) {
    Column(
        modifier = Template10CardHoverStyle.toModifier()
            .fillMaxWidth()
            .fillMaxHeight()
    ) {
        Template10ScrollRod()
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex", "1")
                    property("border-left", "1px solid ${Template10Theme.Line.hex}")
                    property("border-right", "1px solid ${Template10Theme.Line.hex}")
                }
                .backgroundColor(Template10Theme.PaperLight.rgb)
                .padding(top = 48.px, right = 28.px, bottom = 44.px, left = 28.px)
                .gap(16.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Span(
                attrs = Template10GlyphStyle.toModifier()
                    .template10HeadingFont()
                    .fontWeight(FontWeight.ExtraBold)
                    .color(if (benefit.highlighted) Template10Theme.Primary.getColor() else Template10Theme.Ink.rgb)
                    .toAttrs()
            ) {
                Text(benefit.glyph)
            }
            Span(
                attrs = Modifier
                    .template10BodyFont()
                    .fontSize(12.px)
                    .fontWeight(FontWeight.Bold)
                    .letterSpacing(0.3.em)
                    .color(Template10Theme.DeepRed.rgb)
                    .toAttrs()
            ) {
                Text(benefit.pinyin)
            }
            P(
                attrs = Modifier
                    .margin(0.px)
                    .template10HeadingFont()
                    .fontSize(28.px)
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.2)
                    .textAlign(TextAlign.Center)
                    .color(Template10Theme.Ink.rgb)
                    .toAttrs()
            ) {
                Text(LocalText(benefit.title))
            }
            P(
                attrs = Modifier
                    .margin(0.px)
                    .maxWidth(250.px)
                    .template10BodyFont()
                    .fontSize(16.px)
                    .lineHeight(1.6)
                    .textAlign(TextAlign.Center)
                    .color(Template10Theme.Muted.rgb)
                    .toAttrs()
            ) {
                Text(LocalText(benefit.description))
            }
        }
        Template10ScrollRod()
    }
}

@Composable
private fun Template10ScrollRod() {
    Box(
        modifier = Modifier
            .height(14.px)
            .backgroundColor(Template10Theme.Ink.rgb)
            .borderRadius(7.px)
            .styleModifier {
                property("margin-left", "-14px")
                property("margin-right", "-14px")
            }
    )
}
